import {IAngularStatic, IHttpPromise, IHttpResponse, IHttpService, IPromise, IQService} from "angular";
import {OrderSpread, SpreadSummary} from "../../types/OrderSpread";
import {Optional} from "../util/Optional";
import {TmcTicket} from "../../portfolio/templates/ticket/types/OptionsTicket";
import {PortfolioType} from "../../portfolio/models/Portfolio";
import {QuoteService} from "../../quote/service/QuoteService";
import {isOptionQuote, OptionQuoteRecord, QuoteRecord, TmcQuoteRecord} from "../../quote/models/QuoteRecord";
import {Exchange} from "app/instruments/exchange/Exchange";
import {TmcLeg} from "../../portfolio/templates/ticket/services/models/TmcRequests";
import {SecurityUtil} from "../util/SecurityUtil";
import {DateTimeUtil} from "../util/DateTimeUtil";
import {QuoteRequest, TmcQuoteRequest, TmcQuoteRequestLeg} from "../../quote/models/QuoteRequest";
import {PricingInstruction} from "../../orders/models/PricingInstruction";
import {OrderNumber} from "../../orders/models/OrderRecord";
import {
    MultiAccountApiResponse,
    MultiAccountOrderRequest
} from "../../portfolio/templates/ticket/services/models/PlaceMultiAccountOrderRequest";

declare const angular: IAngularStatic;

interface IApiConfig {
    silence?: boolean;
}

export class TradefloorApiService {

    constructor(private $http: IHttpService,
                private $q: IQService,
                private simpleModalService: any,
                private quoteService: QuoteService) {
    }

    public async httpHandler<T>(httpPromise: IPromise<IHttpResponse<T>>,
                                generalErrorMessage?: string | null,
                                silence: boolean = false): Promise<any> {
        try {
            const response = await httpPromise;
            return response.data ? response.data : response;
        } catch (error) {
            if (!silence) {
                const responseData = error.data || {};

                const message = generalErrorMessage || responseData.error || "Server Error";
                await this.simpleModalService.error(null, message);
            }
            return Promise.reject(error);
        }
    }

    public sequenceRun<T>(array: ReadonlyArray<T>, fn: (data: T) => IPromise<any>) {
        const promises = array.map((item) => fn(item));
        const mapped = promises.map((p, index) => this.toResult(p, array[index]));
        return Promise.all(mapped);
    }

    private async toResult<T>(p: IPromise<any>, item: T): Promise<any> {
        try {
            const resp = await p;
            return {success: true, request: item, result: resp};
        } catch (error) {
            return {success: false, request: item, result: error};
        }
    }

    public buildTmc(portfolioId: number, whatifId: number) {
        const url = `/tradefloor/api/portfolio/${portfolioId}/whatifs/${whatifId}/tmc`;
        const promise: IPromise<IHttpResponse<any>> = this.$http
                                                          .post(url, {}, {})
                                                          .then(null as any,
                                                                (error) => {
                                                                    const message = error.data && error.data.error || "Server error";
                                                                    return this.$q.reject({code: error.status, message});
                                                                }) as any;
        return this.httpHandler<any>(promise);
    }

    public placeTmcOrder(portfolioId: number, tmcOrder: TmcTicket): Promise<Array<any>> {
        return this.sequenceRun([tmcOrder], (t) => {
            const postData = {
                code: t.code,
                description: t.description,
                buy: !t.opposite,
                pricingInstruction: PricingInstruction.LIMIT,
                quantity: t.quantity,
                limit: t.limit,
                advice: t.advice,
                legs: t.legs
            };
            return this.$http.post(`/tradefloor/api/portfolio/${portfolioId}/ticket`, postData, {});
        });
    }

    public placeOrder(portfolioId: number, trades: ReadonlyArray<any>, description: string, tmcOverride = false) {
        return this.sequenceRun(trades, (t) => {
            const buy = Optional.of(t.buy)
                                .orElseGet(() => t.quantity > 0);
            const postData = {
                code: t.code,
                exchange: t.exchange,
                description,
                buy,
                pricingInstruction: t.pricingInstruction ?? PricingInstruction.LIMIT,
                quantity: Math.abs(t.quantity),
                limit: t.price || t.limit,
                advice: t.advice,
                legs: [],
                lifetime: t.lifetime,
                tmcOverride,
                conditions: t.conditions,
                isParentOrder: t.isParentOrder,
                childOrders: t.isParentOrder ? t.childOrders : []
            };
            return this.$http.post(`/tradefloor/api/portfolio/${portfolioId}/ticket`, postData, {});
        });
    }

    public placeSingleOrder(portfolioId: number | undefined, t: any, description: string, tmcOverride = false) {
        const buy = Optional.of(t.buy)
                            .orElseGet(() => t.quantity > 0);
        const postData = {
            code: t.code,
            exchange: t.exchange,
            description,
            buy,
            pricingInstruction: t.pricingInstruction ?? PricingInstruction.LIMIT,
            quantity: Math.abs(t.quantity),
            limit: t.price || t.limit,
            advice: t.advice,
            legs: [],
            lifetime: t.lifetime,
            tmcOverride,
            conditions: t.conditions,
            isParentOrder: t.isParentOrder,
            childOrders: t.isParentOrder ? t.childOrders : []
        };
        return this.$http.post(`/tradefloor/api/portfolio/${portfolioId}/ticket`, postData, {});
    }

    public placeChildOrders(portfolioId: number, trades: ReadonlyArray<any>, description: string, tmcOverride = false, parentOrderNumber: OrderNumber) {
        return this.sequenceRun(trades, (t) => {
            const buy = Optional.of(t.buy)
                                .orElseGet(() => t.quantity > 0);
            const postData = {
                code: t.code,
                exchange: t.exchange,
                description,
                buy,
                pricingInstruction: t.pricingInstruction ?? PricingInstruction.LIMIT,
                quantity: Math.abs(t.quantity),
                limit: t.price || t.limit,
                advice: t.advice,
                legs: [],
                lifetime: t.lifetime,
                tmcOverride,
                conditions: t.conditions,
                isParentOrder: t.isParentOrder,
                childOrders: t.childOrders
            };
            return this.$http.post(`/tradefloor/api/portfolio/${portfolioId}/ticket/${parentOrderNumber}/childOrders`, postData, {});
        });
    }

    // public placeMultiOrders(data: MultiAccountOrderRequest): IHttpPromise<MultiAccountApiResponse> | undefined {

    public placeMultiOrders(data: MultiAccountOrderRequest) {
        const orderData = data.orders;
        if (orderData == null) { return; }
        // return this.$http.post(`/tradefloor/api/portfolio/ticket`, postData, {});
        return this.sequenceRun(orderData, (t) => {
            const buy = Optional.of(t.placeOrderRequest.buy)
                                .orElseGet(() => t.placeOrderRequest.quantity > 0);
            const postData = {
                code: t.placeOrderRequest.code,
                exchange: t.placeOrderRequest.exchange,
                description: t.placeOrderRequest.description,
                buy,
                pricingInstruction: t.placeOrderRequest.pricingInstruction ?? PricingInstruction.LIMIT,
                quantity: Math.abs(t.placeOrderRequest.quantity),
                limit: t.placeOrderRequest.limit,
                advice: t.placeOrderRequest.advice,
                legs: [],
                lifetime: t.placeOrderRequest.lifetime,
                tmcOverride: false,
                conditions: t.placeOrderRequest.conditions,
                isParentOrder: t.placeOrderRequest.isParentOrder,
                childOrders: t.placeOrderRequest.isParentOrder ? t.placeOrderRequest.childOrders : []
            };
            return this.$http.post(`/tradefloor/api/portfolio/${t.portfolioId}/ticket`, postData, {});
        });

    }

    public async retrieveOrderSpread(orderSecurities: Array<string>,
                                     portfolioType: PortfolioType | null,
                                     config: IApiConfig = {}): Promise<OrderSpread> {

        const quoteRequests: ReadonlyArray<QuoteRequest> = orderSecurities.map((security) => {
            return {
                code: security,
                exchange: SecurityUtil.guessExchange(security),
                portfolioType: portfolioType || PortfolioType.BROKER
            };
        });

        const quotes = await this.quoteService.quote(quoteRequests);

        return this.mapQuotesToSpread(quotes);
    }

    public async retrieveTmcSpread(tmcCode: string,
                                   legs: Array<TmcLeg>,
                                   opposite: boolean,
                                   portfolioType?: PortfolioType,
                                   config: IApiConfig = {}): Promise<OrderSpread> {
        if (legs == null || legs.length < 1) {
            return Promise.reject("No legs provided");
        }

        const tmcQuoteRequest: TmcQuoteRequest = {
            code: tmcCode,
            exchange: Exchange.AOM,
            portfolioType: portfolioType || PortfolioType.BROKER,
            opposite,
            legs: legs.map((leg) => TradefloorApiService.mapLeg(leg))
        };

        const quote = await this.quoteService.quote(tmcQuoteRequest);

        return TradefloorApiService.mapToSpread(quote as TmcQuoteRecord);
    }

    private static mapLeg(leg: TmcLeg): TmcQuoteRequestLeg {
        return {
            code: leg.code,
            exchange: SecurityUtil.guessExchange(leg.code),
            ratio: leg.ratio
        };
    }

    private mapQuotesToSpread(quotes: ReadonlyArray<QuoteRecord>): OrderSpread {
        const summaries = quotes.map((quote) => TradefloorApiService.mapToSpreadSummary(quote));
        return {
            asAt: DateTimeUtil.isoNow(),
            bidAskSummaryList: summaries,
            underlyingPrice: 0
        };
    }

    private static mapToSpread(tmcQuote: TmcQuoteRecord): OrderSpread {
        const spreadSummaries = tmcQuote.legs
                                        .map(q => TradefloorApiService.mapToSpreadSummary(q))
                                        .concat(TradefloorApiService.mapTmcQuoteToSpreadSummary(tmcQuote));
        return {
            asAt: tmcQuote.asAt,
            bidAskSummaryList: spreadSummaries,
            underlyingPrice: 0
        };
    }

    private static mapToSpreadSummary(quote: QuoteRecord): SpreadSummary {
        if (isOptionQuote(quote)) {
            return TradefloorApiService.mapOptionQuoteToSpreadSummary(quote);
        }

        return TradefloorApiService.mapQuoteToSpreadSummary(quote);
    }

    private static mapTmcQuoteToSpreadSummary(tmcQuote: TmcQuoteRecord): SpreadSummary {
        return {
            code: tmcQuote.code,
            open: tmcQuote.open,
            prevClose: tmcQuote.prevClose,
            ask: tmcQuote.ask,
            askLimit: tmcQuote.askLimit,
            askVol: tmcQuote.askVolume,
            bid: tmcQuote.bid,
            bidLimit: tmcQuote.bidLimit,
            bidVol: tmcQuote.bidVolume,
            change: tmcQuote.change,
            changePct: tmcQuote.changePct,
            theo: tmcQuote.theo,
            mid: tmcQuote.mid,
            basisOfQuotation: tmcQuote.basisOfQuotation,
            tradingState: tmcQuote.tradingState
        };
    }

    private static mapOptionQuoteToSpreadSummary(optionQuote: OptionQuoteRecord): SpreadSummary {
        return {
            code: optionQuote.code,
            open: optionQuote.open,
            prevClose: optionQuote.prevClose,
            ask: optionQuote.ask,
            askLimit: optionQuote.askLimit,
            askVol: optionQuote.askVolume,
            bid: optionQuote.bid,
            bidLimit: optionQuote.bidLimit,
            bidVol: optionQuote.bidVolume,
            change: optionQuote.change,
            changePct: optionQuote.changePct,
            theo: optionQuote.theo,
            mid: optionQuote.mid,
            basisOfQuotation: optionQuote.basisOfQuotation,
            tradingState: optionQuote.tradingState
        };
    }

    private static mapQuoteToSpreadSummary(quote: QuoteRecord): SpreadSummary {
        return {
            code: quote.code,
            open: quote.open,
            prevClose: quote.prevClose,
            ask: quote.ask,
            askVol: quote.askVolume,
            bid: quote.bid,
            bidVol: quote.bidVolume,
            change: quote.change,
            changePct: quote.changePct,
            mid: quote.mid,
            basisOfQuotation: quote.basisOfQuotation,
            tradingState: quote.tradingState
        };
    }
}

const app = angular.module("tf.services");
app.service("tradefloorApi", ["$http", "$q", "simpleModalService", "quoteService", TradefloorApiService]);
